import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useLightbox } from "@/components/Lightbox";
import { toast } from "sonner";
import * as Icons from "lucide-react";

import { eur, dfr, tsfr } from "@/pages/Devis";
import { ClientEquipment } from "@/components/client/ClientEquipment";
import { ResteARegler, WorksiteInvoices } from "@/components/client/ClientInvoices";
const CS_KEY = "cs_token";
const CS_DEVICE = "cs_device";
const CS_IDENT = "cs_ident";
const BANNER = "https://images.unsplash.com/photo-1739129264492-fc983479b85d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
const CLIENT_BG_PORTRAIT = "/signa-client-bg-portrait.webp?v=1";
const CLIENT_BG_LANDSCAPE = "/signa-client-bg-landscape.webp?v=1";
const INK = "#0D2E24";
const STATUS_CLS = {
  planifie: "bg-slate-100 text-slate-600", en_cours: "bg-emerald-100 text-emerald-700",
  en_pause: "bg-amber-100 text-amber-700", termine: "bg-blue-100 text-blue-700",
  annule: "bg-rose-100 text-rose-700",
};
const NAV = [
  { id: "overview", label: "Vue d'ensemble", icon: "LayoutGrid" },
  { id: "sites", label: "Mes sites", icon: "MapPin" },
  { id: "equipements", label: "Équipements", icon: "ShieldCheck" },
  { id: "interventions", label: "Interventions", icon: "CalendarCheck" },
  { id: "devis", label: "Mes devis", icon: "FileSignature" },
  { id: "documents", label: "Documents", icon: "FileText" },
  { id: "messages", label: "Messages", icon: "MessagesSquare" },
];

function Centered({ children }) {
  return <div className="flex min-h-[100svh] flex-col items-center justify-center bg-[#0D2E24] px-6 text-center">{children}</div>;
}
const initials = (name, email) => {
  const base = (name && !name.includes("@")) ? name : (email || "");
  const parts = base.trim().split(/[\s.@]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "CL";
};
const firstName = (name) => (name && !name.includes("@")) ? name.trim().split(/\s+/)[0] : null;

export default function ClientSpace() {
  const [token, setToken] = useState(() => sessionStorage.getItem(CS_KEY));
  const [state, setState] = useState(() =>
    (sessionStorage.getItem(CS_KEY) || (localStorage.getItem(CS_DEVICE) && localStorage.getItem(CS_IDENT))) ? "loading" : "login");
  const [brand, setBrand] = useState({ name: "SIGNA CLEAN", logo_b64: null, slogan: "sécuriser, valoriser, innover", phone: "+596 696 68 77 66", website: "https://www.signaclean.fr" });
  const [me, setMe] = useState(null);
  const [sites, setSites] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [docs, setDocs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [docType, setDocType] = useState("all");
  const [docSearch, setDocSearch] = useState("");
  const [devisList, setDevisList] = useState([]);
  const [summary, setSummary] = useState({ devis_pending: 0, interventions_en_cours: 0, a_venir: 0, rapports: 0 });
  const [devisFilter, setDevisFilter] = useState("all");
  const [devisSite, setDevisSite] = useState("all");
  const [devisQ, setDevisQ] = useState(null);
  const [replyDraft, setReplyDraft] = useState({});
  const [msgReadAt, setMsgReadAt] = useState(() => Number(localStorage.getItem("cs_msg_read")) || 0);
  const [signD, setSignD] = useState(null);
  const [signName, setSignName] = useState("");
  const [signImg, setSignImg] = useState("");
  const [signConsent, setSignConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [refuseD, setRefuseD] = useState(null);
  const [refuseReason, setRefuseReason] = useState("");
  const [holdD, setHoldD] = useState(null);
  const [holdNote, setHoldNote] = useState("");
  const [decBusy, setDecBusy] = useState(false);
  const [highlightDevis, setHighlightDevis] = useState(null);
  const deepLink = useRef((() => {
    const p = new URLSearchParams(window.location.search);
    return { devis: p.get("devis"), view: p.get("view"), chantier: p.get("chantier"), facture: p.get("facture") };
  })());
  const [view, setView] = useState("overview");
  const [intFilter, setIntFilter] = useState("en_cours");
  const [activeSite, setActiveSite] = useState(null);
  const [activeWs, setActiveWs] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqKind, setReqKind] = useState("prestation");
  const [genOpen, setGenOpen] = useState(false);
  const [reqPrefill, setReqPrefill] = useState("");
  const [hlInvoice, setHlInvoice] = useState(null);
  const openInvoiceFor = async (fid, t = token) => {
    try {
      const { data: f } = await axios.get(`${API}/clientspace/invoices/${fid}`, auth(t));
      const { data } = await axios.get(`${API}/clientspace/worksites/${f.worksite_id}`, auth(t));
      setView("interventions"); setActiveSite(null); setActiveWs(data); setHlInvoice(fid);
      setTimeout(() => { const el = document.getElementById(`facture-${fid}`); const m = document.getElementById(`facture-m-${fid}`); (m && m.offsetParent ? m : el)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 900);
    } catch { toast.error("Cette facture n'est pas disponible dans votre espace"); }
  };
  const [eqSite, setEqSite] = useState("");

  const auth = (t) => ({ headers: { Authorization: `Bearer ${t || token}` } });

  useEffect(() => { axios.get(`${API}/clientspace/branding`).then((r) => setBrand((b) => ({ ...b, ...r.data }))).catch(() => {}); }, []);

  const tryDeviceLogin = useCallback(async () => {
    const dev = localStorage.getItem(CS_DEVICE);
    const ident = localStorage.getItem(CS_IDENT);
    if (!dev) return false;
    try {
      const { data } = await axios.post(`${API}/clientspace/auth/device-login`, { identifier: ident || "", device_token: dev });
      sessionStorage.setItem(CS_KEY, data.session_token); setToken(data.session_token);
      return true;
    } catch {
      localStorage.removeItem(CS_DEVICE); localStorage.removeItem(CS_IDENT);
      return false;
    }
  }, []);

  const loadAll = useCallback(async (t) => {
    try {
      const meR = await axios.get(`${API}/clientspace/me`, auth(t));
      setMe(meR.data);
      if (!meR.data.authorized) { setState("unauthorized"); return; }
      const [s, w, d, rq, dv, sm] = await Promise.all([
        axios.get(`${API}/clientspace/sites`, auth(t)),
        axios.get(`${API}/clientspace/worksites`, auth(t)),
        axios.get(`${API}/clientspace/documents`, auth(t)),
        axios.get(`${API}/clientspace/requests`, auth(t)),
        axios.get(`${API}/clientspace/devis`, auth(t)),
        axios.get(`${API}/clientspace/summary`, auth(t)),
      ]);
      setSites(s.data); setWorksites(w.data); setDocs(d.data); setReqs(rq.data || []);
      setDevisList(dv.data || []); setSummary(sm.data || summary); setState("ready");
      const dl = deepLink.current;
      if (dl.devis || dl.view === "devis") {
        const match = dl.devis && dl.devis !== "1" && (dv.data || []).some((x) => x.id === dl.devis);
        if (match || dl.devis === "1" || dl.view === "devis") {
          setActiveSite(null); setActiveWs(null); setView("devis");
          setDevisFilter("all"); setDevisSite("all");
          if (match) setHighlightDevis(dl.devis);
        }
        window.history.replaceState({}, "", window.location.pathname);
        deepLink.current = { devis: null, view: null };
      }
      if (dl.chantier) {
        const wid = dl.chantier;
        window.history.replaceState({}, "", window.location.pathname);
        deepLink.current = { devis: null, view: null, chantier: null };
        try {
          const { data } = await axios.get(`${API}/clientspace/worksites/${wid}`, auth(t));
          setView("interventions"); setActiveSite(null); setActiveWs(data); window.scrollTo(0, 0);
        } catch { toast.error("Ce chantier n'est pas disponible dans votre espace"); }
      }
      if (dl.facture) {
        const fid = dl.facture;
        window.history.replaceState({}, "", window.location.pathname);
        deepLink.current = {};
        await openInvoiceFor(fid, t);
      }
    } catch {
      sessionStorage.removeItem(CS_KEY);
      const ok = await tryDeviceLogin();
      if (!ok) { setToken(null); setState("login"); }
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (token) { setState("loading"); loadAll(token); return; }
    if (sessionStorage.getItem(CS_KEY)) return;
    tryDeviceLogin().then((ok) => { if (!ok) setState("login"); });
    // eslint-disable-next-line
  }, [token]);

  const logout = async () => {
    const dev = localStorage.getItem(CS_DEVICE);
    try { await axios.post(`${API}/clientspace/logout`, { device_token: dev || null }, auth()); } catch { /* ignore */ }
    sessionStorage.removeItem(CS_KEY); localStorage.removeItem(CS_DEVICE); localStorage.removeItem(CS_IDENT);
    setToken(null); setMe(null); setActiveSite(null); setActiveWs(null); setState("login");
  };
  const goView = (v, siteFilter = "") => {
    if (v === "messages") { const t = Date.now(); setMsgReadAt(t); localStorage.setItem("cs_msg_read", String(t)); }
    if (v === "equipements") setEqSite(siteFilter);
    setActiveSite(null); setActiveWs(null); setView(v); window.scrollTo(0, 0);
  };
  const openSite = async (id) => {
    try { const { data } = await axios.get(`${API}/clientspace/sites/${id}`, auth()); setActiveSite(data); setActiveWs(null); window.scrollTo(0, 0); }
    catch { toast.error("Site indisponible"); }
  };
  const openWorksite = async (id) => {
    try { const { data } = await axios.get(`${API}/clientspace/worksites/${id}`, auth()); setActiveWs(data); window.scrollTo(0, 0); }
    catch { toast.error("Chantier indisponible"); }
  };
  const downloadBlob = async (url, filename) => {
    try {
      const { data } = await axios.get(url, { ...auth(), responseType: "blob" });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = href; a.download = filename; a.click();
      URL.revokeObjectURL(href);
    } catch { toast.error("Téléchargement impossible"); }
  };
  const sitePhotoUrl = (p) => !p ? null : (p.kind === "site"
    ? `${API}/clientspace/sites/${p.site_id}/cover?token=${token}`
    : `${API}/clientspace/worksites/${p.ws_id}/file/${p.file_id}?token=${token}`);
  const docUrl = (d) => d.download.kind === "file"
    ? `${API}/clientspace/worksites/${d.download.ws_id}/file/${d.download.fid}`
    : d.download.kind === "vreport" ? `${API}/clientspace/visit-reports/${d.download.rid}/pdf`
    : `${API}/clientspace/sites/${d.download.site_id}/visit/${d.download.vid}/report`;
  const viewBlob = async (url) => {
    try {
      const { data } = await axios.get(url, { ...auth(), responseType: "blob" });
      const href = URL.createObjectURL(data);
      window.open(href, "_blank");
      setTimeout(() => URL.revokeObjectURL(href), 15000);
    } catch { toast.error("Ouverture impossible"); }
  };
  const respondToDate = async (rid, accept) => {
    try {
      await axios.post(`${API}/clientspace/requests/${rid}/respond`, { accept }, auth());
      toast.success(accept ? "Date acceptée. Nous planifions votre intervention." : "Date refusée. Notre équipe vous recontactera.");
      loadAll(token);
    } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); }
  };
  const sendDevisQuestion = async () => {
    if (!devisQ?.msg?.trim()) { toast.error("Écrivez votre question"); return; }
    try {
      await axios.post(`${API}/clientspace/devis/${devisQ.id}/question`, { message: devisQ.msg }, auth());
      toast.success("Question envoyée à l'équipe SIGNA CLEAN"); setDevisQ(null);
      loadAll(token);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
  };
  const sendThreadMessage = async (rid) => {
    const text = (replyDraft[rid] || "").trim();
    if (!text) { toast.error("Écrivez votre message"); return; }
    try {
      await axios.post(`${API}/clientspace/requests/${rid}/message`, { message: text }, auth());
      setReplyDraft((m) => ({ ...m, [rid]: "" }));
      toast.success("Message envoyé");
      loadAll(token);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
  };
  const submitRefuse = async () => {
    setDecBusy(true);
    try {
      await axios.post(`${API}/clientspace/devis/${refuseD.id}/refuse`, { reason: refuseReason }, auth());
      toast.success("Devis refusé. L'équipe SIGNA CLEAN en est informée.");
      setRefuseD(null); loadAll(token);
    } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); } finally { setDecBusy(false); }
  };
  const submitHold = async () => {
    setDecBusy(true);
    try {
      await axios.post(`${API}/clientspace/devis/${holdD.id}/hold`, { reason: holdNote }, auth());
      toast.success("Devis mis en attente. Vous pourrez le signer ou le refuser plus tard.");
      setHoldD(null); loadAll(token);
    } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); } finally { setDecBusy(false); }
  };
  const submitSign = async () => {
    if (!signName.trim()) { toast.error("Indiquez votre nom et prénom"); return; }
    if (!signConsent) { toast.error("Cochez « Bon pour accord »"); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/clientspace/devis/${signD.id}/sign`, { signer_name: signName, signature: signImg, consent: signConsent }, auth());
      toast.success("Devis signé — merci ! Votre accord est enregistré.");
      setSignD(null); setSignName(""); setSignImg(""); setSignConsent(false);
      loadAll(token);
    } catch (e) { toast.error(e.response?.data?.detail || "Signature impossible"); } finally { setSigning(false); }
  };

  const Logo = ({ h = "h-9" }) => brand.logo_b64
    ? <img src={`data:image/png;base64,${brand.logo_b64}`} alt={brand.name} className={`${h} w-auto object-contain`} />
    : <span className="font-display text-xl font-bold text-[#A3E635]">{brand.name}</span>;

  if (state === "login" || state === "init") return (
    <LoginScreen brand={brand} onToken={(t) => { sessionStorage.setItem(CS_KEY, t); setToken(t); }} />
  );
  if (state === "loading") return <Centered><Icons.Loader2 className="h-8 w-8 animate-spin text-emerald-300" /></Centered>;
  if (state === "unauthorized") return (
    <Centered>
      <Icons.ShieldAlert className="h-10 w-10 text-amber-300" />
      <p className="mt-3 font-display text-lg font-semibold text-white" data-testid="cs-unauthorized">Aucun site associé</p>
      <p className="mt-1 max-w-sm text-sm text-emerald-100/80">L'adresse <span className="font-semibold">{me?.email}</span> n'est rattachée à aucun dossier. Contactez SIGNA CLEAN pour associer votre e-mail.</p>
      <Button onClick={logout} variant="outline" className="mt-5 border-white/40 text-white hover:bg-white/10" data-testid="cs-logout">Se déconnecter</Button>
    </Centered>
  );

  const groups = {
    en_cours: worksites.filter((w) => ["en_cours", "en_pause"].includes(w.status)),
    planifie: worksites.filter((w) => w.status === "planifie"),
    termine: worksites.filter((w) => w.status === "termine"),
  };
  const pendingProposals = reqs.filter((r) => r.proposed_date && !r.client_date_response);
  const adminLatest = (r) => Math.max(0, ...((r.messages || []).filter((m) => m.author === "admin").map((m) => Date.parse(m.at) || 0)));
  const msgUnread = reqs.filter((r) => adminLatest(r) > msgReadAt).length;
  const docQ = docSearch.trim().toLowerCase();
  const filteredDocs = docs.filter((d) => {
    const typeOk = docType === "all" || (docType === "report" ? d.type === "Rapport de visite" : d.type !== "Rapport de visite");
    const hay = `${d.title} ${d.site_name || ""} ${d.group_label || ""}`.toLowerCase();
    return typeOk && (!docQ || hay.includes(docQ));
  });
  const fn = firstName(me?.name);

  return (
    <div className="min-h-[100svh] bg-[#F6F8F5]" data-testid="cs-page">
      {/* En-tête */}
      <header className="sticky top-0 z-20 border-b border-black/10 bg-[#0D2E24] text-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6">
          <button onClick={() => goView("overview")} className="shrink-0" data-testid="cs-logo"><Logo /></button>
          <nav className="ml-2 flex flex-1 items-center gap-1 overflow-x-auto sm:ml-6 sm:gap-2">
            {NAV.map((n) => {
              const Ic = Icons[n.icon];
              const active = view === n.id && !activeSite && !activeWs;
              return (
                <button key={n.id} onClick={() => goView(n.id)} data-testid={`cs-nav-${n.id}`}
                  className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${active ? "bg-white/10 text-[#A3E635]" : "text-emerald-100/80 hover:text-white"}`}>
                  <Ic className="h-4 w-4" />{n.label}
                  {n.id === "messages" && msgUnread > 0 && <span className="ml-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white" data-testid="cs-nav-messages-badge">{msgUnread}</span>}
                </button>
              );
            })}
          </nav>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex shrink-0 items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-white/10" data-testid="cs-account">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#A3E635] text-sm font-bold text-[#0D2E24]">{initials(me?.name, me?.email)}</span>
                <span className="hidden text-sm font-medium sm:block">{me?.name || me?.email}</span>
                <Icons.ChevronDown className="hidden h-4 w-4 text-emerald-100/70 sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5 text-xs text-slate-500">{me?.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="cs-logout"><Icons.LogOut className="mr-2 h-4 w-4" />Se déconnecter</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {activeWs ? (
          <WorksiteDetail ws={activeWs} token={token} onBack={() => setActiveWs(null)} downloadBlob={downloadBlob}
            onReload={() => openWorksite(activeWs.id)} onRequest={(k, pre = "") => { setReqKind(k); setReqPrefill(pre); setReqOpen(true); }} hlInvoice={hlInvoice} />
        ) : activeSite ? (
          <SiteView site={activeSite} onBack={() => setActiveSite(null)} onOpenWs={openWorksite} downloadBlob={downloadBlob} onEquipment={() => goView("equipements", activeSite.id)} />
        ) : view === "equipements" ? (
          <ClientEquipment token={token} sites={sites} initialSite={eqSite} />
        ) : view === "overview" ? (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h1 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="cs-greeting">{fn ? `Bonjour ${fn},` : "Bonjour,"}</h1>
                <p className="mt-1 text-slate-500">Vos sites, vos interventions et vos documents, au même endroit.</p>
              </div>
              <Button onClick={() => setGenOpen(true)} className="h-11 bg-emerald-600 px-5 font-semibold hover:bg-emerald-700" data-testid="cs-new-request-btn">
                <Icons.Plus className="mr-1.5 h-5 w-5" />Demander une visite / intervention
              </Button>
            </div>

            {pendingProposals.length > 0 && (
              <Card className="mt-5 border-amber-200 bg-amber-50 p-5" data-testid="cs-proposals">
                <h2 className="flex items-center gap-2 font-display text-base font-semibold text-amber-900"><Icons.CalendarClock className="h-5 w-5" />Nouvelle date proposée</h2>
                <div className="mt-3 space-y-3">
                  {pendingProposals.map((r) => (
                    <div key={r.id} className="rounded-lg bg-white p-4 ring-1 ring-amber-100" data-testid={`cs-proposal-${r.id}`}>
                      <p className="text-sm text-slate-700"><span className="font-medium">{r.site_display}</span>{r.prestation_label ? ` · ${r.prestation_label}` : ""}</p>
                      <p className="mt-1 text-sm text-slate-600">Date proposée : <strong>{r.proposed_date}</strong></p>
                      {r.admin_message && <p className="mt-2 rounded bg-slate-50 p-2 text-sm text-slate-600">{r.admin_message}</p>}
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => respondToDate(r.id, true)} data-testid={`cs-proposal-accept-${r.id}`}>Accepter cette date</Button>
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={() => respondToDate(r.id, false)} data-testid={`cs-proposal-refuse-${r.id}`}>Refuser</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Bandeau de marque */}
            <div className="relative mt-5 overflow-hidden rounded-2xl" style={{ backgroundColor: INK }} data-testid="cs-banner">
              <div className="absolute inset-y-0 right-0 w-2/3 bg-cover bg-center opacity-90" style={{ backgroundImage: `url(${BANNER})` }} />
              <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${INK} 38%, ${INK}cc 55%, transparent 85%)` }} />
              <div className="relative px-6 py-8 sm:px-10 sm:py-10">
                <p className="text-xs font-medium uppercase tracking-wide text-[#A3E635]">Votre espace SIGNA CLEAN</p>
                <p className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">sécuriser, valoriser, <span className="text-[#A3E635]">innover</span></p>
              </div>
            </div>

            {/* Indicateurs */}
            <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <button onClick={() => { setDevisFilter("pending"); goView("devis"); }} data-testid="cs-stat-devis"
                className={`flex items-center gap-4 rounded-xl border p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${summary.devis_pending > 0 ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white"}`}>
                <span className={`flex h-12 w-12 items-center justify-center rounded-full ${summary.devis_pending > 0 ? "bg-orange-100 text-orange-600" : "bg-slate-50 text-slate-400"}`}><Icons.FileClock className="h-6 w-6" /></span>
                <div><p className={`font-display text-2xl font-bold ${summary.devis_pending > 0 ? "text-orange-600" : "text-slate-900"}`}>{summary.devis_pending}</p><p className="text-sm text-slate-500">Devis en attente</p></div>
              </button>
              <StatCard icon="Activity" value={summary.interventions_en_cours} label="Intervention en cours"
                onClick={() => { setIntFilter("en_cours"); goView("interventions"); }} testid="cs-stat-encours" />
              <StatCard icon="CalendarClock" value={summary.a_venir} label="À venir"
                onClick={() => { setIntFilter("planifie"); goView("interventions"); }} testid="cs-stat-avenir" />
              <StatCard icon="FileText" value={summary.rapports} label="Rapports disponibles"
                onClick={() => goView("documents")} testid="cs-stat-rapports" />
            </div>

            <div className="mt-5"><ResteARegler token={token} onOpenInvoice={(id) => openInvoiceFor(id)} /></div>

            {/* Contenu principal deux colonnes */}
            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
              <Card className="p-5">
                <SectionHead title="Mes sites" action={sites.length > 0 ? "Voir tous mes sites" : null} onAction={() => goView("sites")} testid="cs-see-sites" />
                {sites.length === 0 ? <Empty text="Aucun site pour le moment." />
                  : <SiteCard site={sites[0]} photo={sitePhotoUrl(sites[0].photo)} onOpen={() => openSite(sites[0].id)} />}
              </Card>

              <div className="space-y-5">
                <Card className="p-5">
                  <SectionHead title="Mes devis à consulter" action={devisList.length > 0 ? "Voir tous mes devis" : null} onAction={() => goView("devis")} testid="cs-see-devis" />
                  {devisList.filter((d) => d.status === "pending").length === 0 ? <Empty text="Aucun devis en attente." />
                    : <div className="space-y-2">{devisList.filter((d) => d.status === "pending").slice(0, 2).map((d) => (
                        <button key={d.id} onClick={() => goView("devis")} className="flex w-full items-center justify-between gap-2 rounded-lg p-2 text-left hover:bg-slate-50" data-testid={`cs-home-devis-${d.id}`}>
                          <span className="flex min-w-0 items-center gap-2"><Icons.FileText className="h-5 w-5 shrink-0 text-rose-400" /><span className="min-w-0"><span className="block truncate font-medium text-slate-800">{d.title}</span><span className="block truncate text-xs text-slate-500">{d.site_name}</span></span></span>
                          <span className="shrink-0 text-right"><span className="block font-semibold text-slate-900">{eur(d.amount_ttc)}</span><span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-700">En attente</span></span>
                        </button>
                      ))}</div>}
                </Card>
                <Card className="p-5">
                  <SectionHead title="Suivi des interventions" action={groups.en_cours.length > 0 ? "Voir toutes les interventions" : null} onAction={() => { setIntFilter("en_cours"); goView("interventions"); }} testid="cs-see-int" />
                  {groups.en_cours.length === 0 ? <Empty text="Aucune intervention en cours." />
                    : <IntRow w={groups.en_cours[0]} onOpen={() => openWorksite(groups.en_cours[0].id)} />}
                </Card>
                <Card className="p-5">
                  <SectionHead title="Dernier document" action={docs.length > 0 ? "Voir tous les documents" : null} onAction={() => goView("documents")} testid="cs-see-docs" />
                  {docs.length === 0 ? <Empty text="Aucun document disponible." />
                    : <DocRow d={docs[0]} onDownload={() => downloadBlob(docUrl(docs[0]), `${docs[0].title}.pdf`)} />}
                </Card>
              </div>
            </div>
          </>
        ) : view === "sites" ? (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Mes sites</h1>
            {sites.length === 0 ? <Empty text="Aucun site pour le moment." />
              : <div className="mt-4 grid gap-4 sm:grid-cols-2">{sites.map((s) => (
                  <Card key={s.id} className="p-4" data-testid={`cs-site-${s.id}`}><SiteCard site={s} photo={sitePhotoUrl(s.photo)} onOpen={() => openSite(s.id)} /></Card>
                ))}</div>}
          </>
        ) : view === "interventions" ? (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Interventions</h1>
            <div className="mt-3 flex gap-2">
              {[["en_cours", `En cours (${groups.en_cours.length})`], ["planifie", `À venir (${groups.planifie.length})`], ["termine", `Terminés (${groups.termine.length})`]].map(([k, lbl]) => (
                <button key={k} onClick={() => setIntFilter(k)} data-testid={`cs-int-filter-${k}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${intFilter === k ? "bg-emerald-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>{lbl}</button>
              ))}
            </div>
            <div className="mt-4">
              {groups[intFilter].length === 0 ? <Empty text="Aucune intervention dans cette catégorie." />
                : <div className="space-y-3">{groups[intFilter].map((w) => (
                    <Card key={w.id} className="p-4" data-testid={`cs-ws-${w.id}`}><IntRow w={w} onOpen={() => openWorksite(w.id)} /></Card>
                  ))}</div>}
            </div>
          </>
        ) : view === "devis" ? (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Mes devis</h1>
            <p className="text-sm text-slate-500">Consultez vos propositions et échangez avec SIGNA CLEAN.</p>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                {[["all", "Tous"], ["pending", "En attente"], ["accepted", "Acceptés"], ["refused", "Refusés"], ["expired", "Expirés"]].map(([k, lbl]) => {
                  const n = k === "all" ? devisList.length : devisList.filter((d) => d.status === k).length;
                  return <button key={k} onClick={() => setDevisFilter(k)} data-testid={`cs-devis-filter-${k}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${devisFilter === k ? "bg-emerald-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>{lbl} ({n})</button>;
                })}
              </div>
              <select value={devisSite} onChange={(e) => setDevisSite(e.target.value)} className="h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="cs-devis-site">
                <option value="all">Tous mes sites</option>
                {[...new Map(devisList.map((d) => [d.site_id, d.site_name])).entries()].map(([sid, sn]) => <option key={sid} value={sid}>{sn}</option>)}
              </select>
            </div>
            {(() => {
              const list = devisList.filter((d) => (devisFilter === "all" || d.status === devisFilter) && (devisSite === "all" || d.site_id === devisSite));
              return list.length === 0 ? <Empty text="Aucun devis dans cette catégorie." />
                : <div className="mt-4 space-y-3">{list.map((d) => (
                    <DevisCard key={d.id} d={d} highlight={highlightDevis === d.id}
                      onView={() => downloadBlob(`${API}/clientspace/devis/${d.id}/pdf`, `${d.reference}.pdf`)}
                      onDownload={() => downloadBlob(`${API}/clientspace/devis/${d.id}/pdf`, `${d.reference}.pdf`)}
                      onQuestion={() => setDevisQ({ id: d.id, ref: d.reference, msg: "" })}
                      onReport={() => d.report && downloadBlob(`${API}/clientspace/sites/${d.report.site_id}/visit/${d.report.vid}/report`, `rapport-${d.reference}.pdf`)}
                      onSign={() => { setSignD({ id: d.id, ref: d.reference, title: d.title, amount: d.amount_ttc }); setSignName(me?.name && !me.name.includes("@") ? me.name : ""); setSignImg(""); setSignConsent(false); }}
                      onRefuse={() => { setRefuseD({ id: d.id, ref: d.reference }); setRefuseReason(""); }}
                      onHold={() => { setHoldD({ id: d.id, ref: d.reference }); setHoldNote(""); }}
                      onWorksite={() => d.worksite && openWorksite(d.worksite.id)}
                      onVReport={(r, dl) => (dl ? downloadBlob(`${API}/clientspace/visit-reports/${r.id}/pdf`, `${r.title || "rapport"}.pdf`) : viewBlob(`${API}/clientspace/visit-reports/${r.id}/pdf`))} />
                  ))}</div>;
            })()}
            <div className="mt-5 flex items-start gap-2 rounded-lg bg-slate-50 p-4 text-sm text-slate-600" data-testid="cs-devis-info">
              <Icons.Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <span><strong>Pour donner votre accord</strong>, transmettez votre devis signé à SIGNA CLEAN via votre canal de contact habituel.</span>
            </div>
          </>
        ) : view === "documents" ? (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Documents</h1>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                {[["all", "Tous"], ["report", "Rapports de visite"], ["chantier", "Documents chantier"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setDocType(k)} data-testid={`cs-doc-filter-${k}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${docType === k ? "bg-emerald-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>{lbl}</button>
                ))}
              </div>
              <div className="relative sm:w-72">
                <Icons.Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input value={docSearch} onChange={(e) => setDocSearch(e.target.value)} placeholder="Rechercher par chantier ou site…"
                  className="h-10 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="cs-doc-search" />
              </div>
            </div>
            {docs.length === 0 ? <Empty text="Aucun document disponible." />
              : filteredDocs.length === 0 ? <Empty text="Aucun document ne correspond à votre recherche." />
              : <div className="mt-4 space-y-6">{groupDocs(filteredDocs).map((g) => (
                  <div key={g.key} data-testid={`cs-docgroup-${g.key}`}>
                    <div className="mb-2 flex items-center gap-2">
                      <Icons.FolderClosed className="h-4 w-4 text-emerald-600" />
                      <h2 className="font-display text-sm font-semibold text-slate-700">{g.label}</h2>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{g.items.length}</span>
                    </div>
                    <div className="space-y-2">{g.items.map((d) => (
                      <Card key={d.key} className="flex items-center justify-between gap-3 p-4" data-testid={`cs-docitem-${d.key}`}>
                        <DocRow d={d} compact onDownload={() => downloadBlob(docUrl(d), `${d.title}.pdf`)} />
                      </Card>
                    ))}</div>
                  </div>
                ))}</div>}
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Messages</h1>
            <p className="text-sm text-slate-500">Vos échanges avec l'équipe SIGNA CLEAN.</p>
            {(() => {
              const threads = reqs.filter((r) => (r.messages || []).length > 0);
              return threads.length === 0 ? <div className="mt-4"><Empty text="Aucun échange pour le moment. Posez une question depuis un devis pour démarrer la discussion." /></div>
                : <div className="mt-4 space-y-4">{threads.map((r) => (
                    <Card key={r.id} className="p-5" data-testid={`cs-thread-${r.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Icons.MessagesSquare className="h-4 w-4 text-emerald-600" />
                        <h2 className="font-display text-base font-semibold text-slate-800">{r.subject || "Demande"}</h2>
                        {r.site_display && r.site_display !== "—" && <span className="text-sm text-slate-400">· {r.site_display}</span>}
                      </div>
                      <div className="mt-3 space-y-2">
                        {(r.messages || []).map((m, i) => (
                          <div key={i} className={`flex ${m.author === "admin" ? "justify-start" : "justify-end"}`} data-testid={`cs-thread-${r.id}-msg-${i}`}>
                            <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${m.author === "admin" ? "bg-emerald-50 text-emerald-900" : "bg-slate-100 text-slate-700"}`}>
                              <div className="mb-0.5 text-[11px] font-medium opacity-70">{m.author === "admin" ? "SIGNA CLEAN" : "Vous"}{m.at ? ` · ${tsfr(m.at)}` : ""}</div>
                              {m.text}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 flex items-end gap-2 border-t border-slate-100 pt-3">
                        <textarea value={replyDraft[r.id] || ""} onChange={(e) => setReplyDraft((mm) => ({ ...mm, [r.id]: e.target.value }))} rows={2}
                          placeholder="Votre message…" className="flex-1 rounded-lg border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid={`cs-thread-input-${r.id}`} />
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => sendThreadMessage(r.id)} data-testid={`cs-thread-send-${r.id}`}><Icons.Send className="h-4 w-4" /></Button>
                      </div>
                    </Card>
                  ))}</div>;
            })()}
          </>
        )}
      </main>

      {/* Pied de page contact */}
      <footer className="mt-8 border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Icons.Headphones className="h-5 w-5" /></span>
            <div><p className="font-semibold text-slate-800">Une question sur votre intervention ?</p><p className="text-sm text-slate-500">Notre équipe est à votre écoute.</p></div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <a href={`tel:${(brand.phone || "").replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 text-slate-700 hover:text-emerald-700" data-testid="cs-contact-phone"><Icons.Phone className="h-4 w-4 text-emerald-600" />{brand.phone}</a>
            <a href={brand.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-emerald-700" data-testid="cs-contact-web"><Icons.Globe className="h-4 w-4 text-emerald-600" />{(brand.website || "").replace(/^https?:\/\//, "")}</a>
          </div>
        </div>
      </footer>

      <RequestDialog open={reqOpen} onOpenChange={setReqOpen} kind={reqKind} prefill={reqPrefill} wsId={activeWs?.id} auth={auth}
        onDone={() => activeWs && openWorksite(activeWs.id)} />
      <GeneralRequestDialog open={genOpen} onOpenChange={setGenOpen} auth={auth} />
      <Dialog open={!!devisQ} onOpenChange={(o) => !o && setDevisQ(null)}>
        <DialogContent data-testid="cs-devisq-dialog">
          <DialogHeader><DialogTitle>Poser une question</DialogTitle>
            <DialogDescription>À propos du devis {devisQ?.ref}. Votre message est transmis à l'équipe SIGNA CLEAN.</DialogDescription></DialogHeader>
          <textarea value={devisQ?.msg || ""} onChange={(e) => setDevisQ((q) => ({ ...q, msg: e.target.value }))} rows={4}
            className="w-full rounded-lg border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Votre question…" data-testid="cs-devisq-msg" />
          <DialogFooter><Button variant="outline" onClick={() => setDevisQ(null)}>Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={sendDevisQuestion} data-testid="cs-devisq-send">Envoyer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!signD} onOpenChange={(o) => !o && setSignD(null)}>
        <DialogContent data-testid="cs-sign-dialog">
          <DialogHeader><DialogTitle>Signer le devis</DialogTitle>
            <DialogDescription>Devis {signD?.ref}{signD?.title ? ` — ${signD.title}` : ""}{signD?.amount != null ? ` · ${eur(signD.amount)}` : ""}. Votre signature vaut acceptation (signature électronique simple).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Nom et prénom du signataire</label>
              <input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Ex. Jean Dupont"
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="cs-sign-name" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Votre signature</label>
              <SignaturePad onChange={setSignImg} />
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700" data-testid="cs-sign-consent">
              <input type="checkbox" checked={signConsent} onChange={(e) => setSignConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600" data-testid="cs-sign-consent-cb" />
              <span>Je déclare <strong>« Bon pour accord »</strong> et j'accepte ce devis.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignD(null)}>Annuler</Button>
            <Button className="bg-[#0D2E24] hover:bg-[#0D2E24]/90" onClick={submitSign} disabled={signing} data-testid="cs-sign-submit">
              {signing ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Valider ma signature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!refuseD} onOpenChange={(o) => !o && setRefuseD(null)}>
        <DialogContent data-testid="cs-refuse-dialog">
          <DialogHeader><DialogTitle>Refuser le devis {refuseD?.ref}</DialogTitle>
            <DialogDescription>Vous pouvez indiquer un motif (facultatif). L'équipe SIGNA CLEAN sera notifiée.</DialogDescription></DialogHeader>
          <textarea value={refuseReason} onChange={(e) => setRefuseReason(e.target.value)} rows={3}
            placeholder="Motif du refus (facultatif)…" className="w-full rounded-lg border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400" data-testid="cs-refuse-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefuseD(null)}>Annuler</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={submitRefuse} disabled={decBusy} data-testid="cs-refuse-submit">
              {decBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Refuser le devis"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!holdD} onOpenChange={(o) => !o && setHoldD(null)}>
        <DialogContent data-testid="cs-hold-dialog">
          <DialogHeader><DialogTitle>Mettre le devis {holdD?.ref} en attente</DialogTitle>
            <DialogDescription>Le devis reste disponible : vous pourrez le signer ou le refuser plus tard. Un message peut être ajouté (facultatif).</DialogDescription></DialogHeader>
          <textarea value={holdNote} onChange={(e) => setHoldNote(e.target.value)} rows={3}
            placeholder="Message à l'équipe (facultatif)…" className="w-full rounded-lg border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" data-testid="cs-hold-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoldD(null)}>Annuler</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={submitHold} disabled={decBusy} data-testid="cs-hold-submit">
              {decBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Mettre en attente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SectionHead = ({ title, action, onAction, testid }) => (
  <div className="mb-3 flex items-center justify-between gap-2">
    <h2 className="font-display text-base font-semibold text-slate-800">{title}</h2>
    {action && <button onClick={onAction} className="flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline" data-testid={testid}>{action}<Icons.ChevronRight className="h-4 w-4" /></button>}
  </div>
);
const Empty = ({ text }) => <p className="py-6 text-center text-sm text-slate-400">{text}</p>;

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const sx = ref.current.width / r.width, sy = ref.current.height / r.height;
    return [((t ? t.clientX : e.clientX) - r.left) * sx, ((t ? t.clientY : e.clientY) - r.top) * sy];
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext("2d"); const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.strokeStyle = "#0D2E24"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(""); };
  return (
    <div>
      <canvas ref={ref} width={480} height={160} className="w-full touch-none rounded-lg border border-slate-300 bg-slate-50"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="cs-sign-canvas" />
      <button type="button" onClick={clear} className="mt-1 text-xs text-slate-500 hover:underline" data-testid="cs-sign-clear">Effacer</button>
    </div>
  );
}
const STEP_ICON = { devis_accepte: "FileCheck2", planifiee: "CalendarCheck", en_cours: "Loader", terminee: "Flag" };
const fmtMartinique = (iso) => {
  if (!iso) return null;
  try { return new Intl.DateTimeFormat("fr-FR", { timeZone: "America/Martinique", dateStyle: "long", timeStyle: "short" }).format(new Date(iso)); }
  catch { return null; }
};
const fmtDateFr = (d) => {
  if (!d) return null;
  try { return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" }).format(new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? d + "T00:00:00" : d)); }
  catch { return d; }
};

function Stepper({ progress, compact }) {
  if (!progress) return null;
  const { steps = [], current_label, confirmed_date, last_update, paused, cancelled, report_state } = progress;
  const dotCls = (state) => {
    if (cancelled && state !== "done") return "border-slate-200 bg-white text-slate-300";
    if (state === "done") return "border-[#0D3B2E] bg-[#0D3B2E] text-white";
    if (state === "current") return "border-emerald-500 bg-emerald-500 text-white ring-4 ring-emerald-100";
    return "border-slate-200 bg-white text-slate-300";
  };
  const connCls = (prevDone) => (!cancelled && prevDone ? "bg-[#0D3B2E]" : "bg-slate-200");
  const lblCls = (state) => {
    if (cancelled && state !== "done") return "text-slate-400";
    if (state === "done") return "text-[#0D3B2E] font-semibold";
    if (state === "current") return "text-emerald-700 font-semibold";
    return "text-slate-500";
  };
  const Dot = ({ s }) => {
    const I = Icons[STEP_ICON[s.key]] || Icons.Circle;
    return (
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${dotCls(s.state)}`}>
        {s.state === "done" ? <Icons.Check className="h-4 w-4" /> : <I className="h-4 w-4" />}
      </span>
    );
  };
  return (
    <div data-testid="cs-stepper">
      {(paused || cancelled) && (
        <div className="mb-3">
          {paused && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700" data-testid="cs-stepper-paused"><Icons.PauseCircle className="h-3.5 w-3.5" />En pause</span>}
          {cancelled && <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700" data-testid="cs-stepper-cancelled"><Icons.XCircle className="h-3.5 w-3.5" />Annulée</span>}
        </div>
      )}
      {/* Tablette / ordinateur : horizontal */}
      <ol className="hidden sm:flex sm:items-start">
        {steps.map((s, i) => (
          <li key={s.key} className="relative flex flex-1 flex-col items-center text-center">
            {i > 0 && <span className={`absolute left-[-50%] top-4 h-0.5 w-full ${connCls(steps[i - 1].state === "done")}`} aria-hidden="true" />}
            <span className="relative z-10"><Dot s={s} /></span>
            <span className={`mt-2 px-1 text-xs leading-tight ${lblCls(s.state)}`}>{s.label}</span>
          </li>
        ))}
      </ol>
      {/* Mobile : vertical, libellés complets sans défilement */}
      <ol className="sm:hidden">
        {steps.map((s, i) => (
          <li key={s.key} className="flex gap-3">
            <div className="flex flex-col items-center">
              <Dot s={s} />
              {i < steps.length - 1 && <span className={`my-1 h-5 w-0.5 ${connCls(s.state === "done")}`} aria-hidden="true" />}
            </div>
            <span className={`pt-1.5 text-sm ${lblCls(s.state)}`}>{s.label}</span>
          </li>
        ))}
      </ol>
      {compact ? (
        <p className="mt-3 text-xs font-semibold text-emerald-700" data-testid="cs-stepper-status">{current_label}</p>
      ) : (
        <div className="mt-4 space-y-1 border-t border-slate-100 pt-3 text-sm" data-testid="cs-stepper-meta">
          <p className="font-medium text-slate-700">Statut&nbsp;: <span className="text-emerald-700">{current_label}</span></p>
          {confirmed_date && <p className="text-slate-500" data-testid="cs-stepper-date">Date confirmée&nbsp;: {fmtDateFr(confirmed_date)}</p>}
          {last_update && <p className="text-slate-500" data-testid="cs-stepper-update">Dernière mise à jour&nbsp;: {fmtMartinique(last_update)}</p>}
          {report_state === "available" && <p className="flex items-center gap-1.5 font-medium text-emerald-700" data-testid="cs-stepper-report"><Icons.FileCheck2 className="h-4 w-4" />Rapport disponible</p>}
          {report_state === "preparation" && <p className="flex items-center gap-1.5 text-slate-500" data-testid="cs-stepper-report"><Icons.Clock className="h-4 w-4" />Rapport en préparation</p>}
        </div>
      )}
    </div>
  );
}
const DEVIS_BADGE = { pending: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700", refused: "bg-rose-100 text-rose-700", expired: "bg-rose-50 text-rose-500" };
const DEVIS_LBL = { pending: "En attente de réponse", accepted: "Accepté", refused: "Refusé", expired: "Expiré" };
function DevisCard({ d, onView, onDownload, onQuestion, onWorksite, onReport, onSign, onRefuse, onHold, onVReport, highlight }) {
  const ref = useRef(null);
  useEffect(() => {
    if (highlight && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight]);
  return (
    <Card ref={ref} className={`p-5 transition-shadow ${highlight ? "ring-2 ring-emerald-500 shadow-md" : ""}`} data-testid={`cs-devis-${d.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${DEVIS_BADGE[d.status] || "bg-slate-100 text-slate-600"}`}>{DEVIS_LBL[d.status] || d.status}</span>
            {d.status === "pending" && d.client_hold && <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700" data-testid={`cs-devis-hold-${d.id}`}>En attente de votre décision</span>}
            <span className="font-semibold text-slate-800">{d.title}</span>
          </div>
          <p className="mt-0.5 text-sm text-slate-500">{d.site_name}</p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            <span>Réf. {d.reference}</span><span>Émis le {dfr(d.issue_date)}</span>
            {d.validity_date && <span>Valable jusqu'au {dfr(d.validity_date)}</span>}
          </div>
        </div>
        <div className="text-right"><div className="font-display text-lg font-bold text-slate-900">{eur(d.amount_ttc)}</div>
          {d.worksite && <span className="text-xs text-emerald-700">Intervention associée</span>}</div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={onView} data-testid={`cs-devis-view-${d.id}`}><Icons.FileText className="mr-1.5 h-4 w-4" />Consulter le devis</Button>
        <Button size="sm" variant="ghost" onClick={onDownload} data-testid={`cs-devis-dl-${d.id}`}><Icons.Download className="mr-1.5 h-4 w-4" />Télécharger</Button>
        {d.report && <Button size="sm" variant="ghost" className="text-emerald-700" onClick={onReport} data-testid={`cs-devis-report-${d.id}`}><Icons.ClipboardCheck className="mr-1.5 h-4 w-4" />Voir le rapport de visite</Button>}
        {d.status === "pending" && <Button size="sm" className="bg-[#0D2E24] hover:bg-[#0D2E24]/90" onClick={onSign} data-testid={`cs-devis-sign-${d.id}`}><Icons.PenLine className="mr-1.5 h-4 w-4" />Signer le devis</Button>}
        {d.status === "pending" && !d.client_hold && <Button size="sm" variant="outline" onClick={onHold} data-testid={`cs-devis-hold-btn-${d.id}`}><Icons.Clock className="mr-1.5 h-4 w-4" />Mettre en attente</Button>}
        {d.status === "pending" && <Button size="sm" variant="outline" className="text-rose-600" onClick={onRefuse} data-testid={`cs-devis-refuse-btn-${d.id}`}><Icons.X className="mr-1.5 h-4 w-4" />Refuser</Button>}
        {d.worksite
          ? <Button size="sm" variant="ghost" className="text-emerald-700" onClick={onWorksite} data-testid={`cs-devis-ws-${d.id}`}><Icons.CalendarCheck className="mr-1.5 h-4 w-4" />Voir l'intervention</Button>
          : <Button size="sm" variant="ghost" onClick={onQuestion} data-testid={`cs-devis-q-${d.id}`}><Icons.MessageCircle className="mr-1.5 h-4 w-4" />Poser une question</Button>}
      </div>
      {d.visit_reports?.length > 0 && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3" data-testid={`cs-devis-vreports-${d.id}`}>
          <p className="mb-1.5 text-xs font-semibold uppercase text-slate-500">Rapports de visite</p>
          <ul className="space-y-1.5">{d.visit_reports.map((r) => (
            <li key={r.id} className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-2" data-testid={`cs-vreport-${r.id}`}>
              <span className="flex min-w-0 flex-1 items-start gap-2 text-slate-700"><Icons.ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /><span>{r.title}{r.visit_date ? <span className="text-xs text-slate-500"> · visite du {dfr(r.visit_date)}</span> : null}</span></span>
              <span className="flex gap-1 pl-5 sm:pl-0">
              <Button size="sm" variant="ghost" className="h-7 text-emerald-700" onClick={() => onVReport(r, false)} data-testid={`cs-vreport-view-${r.id}`}>Consulter</Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => onVReport(r, true)} data-testid={`cs-vreport-dl-${r.id}`}><Icons.Download className="mr-1 h-3.5 w-3.5" />Télécharger</Button></span>
            </li>))}</ul>
        </div>)}
    </Card>
  );
}

function StatCard({ icon, value, label, onClick, testid }) {
  const Ic = Icons[icon];
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Ic className="h-6 w-6" /></span>
      <div><p className="font-display text-2xl font-bold text-slate-900">{value}</p><p className="text-sm text-slate-500">{label}</p></div>
    </button>
  );
}

function SiteCard({ site, photo, onOpen }) {
  return (
    <div>
      <div className="relative overflow-hidden rounded-xl">
        {photo
          ? <img src={photo} alt="" className="h-44 w-full object-cover" onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
          : null}
        <div className="flex h-44 w-full items-center justify-center bg-slate-100" style={{ display: photo ? "none" : "flex" }}>
          <Icons.Building2 className="h-12 w-12 text-slate-300" />
        </div>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5"><Icons.MapPin className="h-4 w-4 text-emerald-600" /><p className="font-semibold text-slate-800">{site.name}</p></div>
          <p className="ml-5 text-sm text-slate-500">{site.commune || site.address || ""}</p>
          <div className="ml-5 mt-1 flex gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Icons.Activity className="h-3.5 w-3.5" />{site.worksites_count} chantier{site.worksites_count > 1 ? "s" : ""}</span>
            <span className="flex items-center gap-1"><Icons.FileText className="h-3.5 w-3.5" />{site.reports_count} rapport{site.reports_count > 1 ? "s" : ""}</span>
          </div>
        </div>
        <button onClick={onOpen} className="flex shrink-0 items-center gap-1 text-sm font-medium text-emerald-700 hover:underline" data-testid={`cs-opensite-${site.id}`}>Consulter le site<Icons.ArrowRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function IntRow({ w, onOpen }) {
  return (
    <div className="rounded-xl bg-emerald-50/40 p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-emerald-600 shadow-sm"><Icons.Activity className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[w.status] || "bg-slate-100 text-slate-600"}`}>{w.status_label}</span>
          <p className="mt-1 font-semibold text-slate-800">{w.title}</p>
          <p className="text-xs text-slate-500">{w.site_name}</p>
          <p className="mt-1 text-sm text-slate-600">{w.last_info || "Retrouvez les informations publiées sur votre chantier."}</p>
          <div className="mt-3"><Stepper progress={w.progress} compact /></div>
        </div>
      </div>
      <div className="mt-2 border-t border-emerald-100 pt-2 text-center">
        <button onClick={onOpen} className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline" data-testid={`cs-opensuivi-${w.id}`}>Voir le suivi<Icons.ArrowRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function DocRow({ d, onDownload, compact }) {
  return (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-500"><Icons.FileText className="h-5 w-5" /></span>
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-800">{d.title}</p>
          <p className="truncate text-sm text-slate-500">{[d.type, d.site_name, d.date].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      <button onClick={onDownload} className={`flex shrink-0 items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline ${compact ? "" : "mt-3 sm:mt-0"}`} data-testid={`cs-dl-${d.key}`}><Icons.Download className="h-4 w-4" />Télécharger</button>
    </>
  );
}

function SiteView({ site, onBack, onOpenWs, downloadBlob, onEquipment }) {
  const reportUrl = (vid) => `${API}/clientspace/sites/${site.id}/visit/${vid}/report`;
  return (
    <div className="space-y-4" data-testid="cs-site-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="cs-site-back"><Icons.ChevronLeft className="h-4 w-4" />Retour</button>
      <Card className="p-5">
        <h1 className="font-display text-2xl font-bold text-slate-900">{site.name}</h1>
        <p className="text-sm text-slate-500">{[site.address, site.commune].filter(Boolean).join(", ")}</p>
        <Button onClick={onEquipment} variant="outline" className="mt-3" data-testid="cs-site-equipment-btn"><Icons.ShieldCheck className="mr-1.5 h-4 w-4" />Équipements de ce site ({site.equipment_count || 0})</Button>
      </Card>
      <Card className="p-4">
        <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Rapports de visite</h2>
        {(!site.visit_reports || site.visit_reports.length === 0) ? <Empty text="Aucun rapport publié pour ce site." />
          : <ul className="space-y-1.5">{site.visit_reports.map((r) => (
              <li key={r.id}><button onClick={() => downloadBlob(reportUrl(r.id), `rapport-${r.reference}.pdf`)} className="flex items-center gap-2 text-sm text-emerald-700 hover:underline" data-testid={`cs-visit-report-${r.id}`}><Icons.FileText className="h-4 w-4" />{r.reference}{r.date ? ` — ${r.date}` : ""}</button></li>
            ))}</ul>}
      </Card>
      <Card className="p-4">
        <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Suivi des interventions</h2>
        {(!site.worksites || site.worksites.length === 0) ? <Empty text="Aucune intervention sur ce site." />
          : <div className="space-y-2">{site.worksites.map((w) => (
              <button key={w.id} onClick={() => onOpenWs(w.id)} data-testid={`cs-ws-${w.id}`}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/40">
                <div className="min-w-0"><span className="font-mono-data text-xs text-slate-400">{w.reference}</span><p className="font-medium text-slate-800">{w.title}</p></div>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CLS[w.status] || "bg-slate-100 text-slate-600"}`}>{w.status_label}</span>
              </button>
            ))}</div>}
      </Card>
    </div>
  );
}

function WorksiteDetail({ ws, token, onBack, downloadBlob, onReload, onRequest, hlInvoice }) {
  const lb = useLightbox();
  const [uploading, setUploading] = useState(false);
  const fileUrl = (fid) => `${API}/clientspace/worksites/${ws.id}/file/${fid}`;
  const imgUrl = (fid) => `${fileUrl(fid)}?token=${token}`;
  const reportUrl = (rid) => `${API}/clientspace/worksites/${ws.id}/report/${rid}`;
  const onUpload = async (e) => {
    const file = (e.target.files || [])[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("caption", file.name);
    try {
      await axios.post(`${API}/clientspace/worksites/${ws.id}/upload`, fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      toast.success("Document envoyé"); onReload();
    } catch { toast.error("Envoi impossible"); } finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-4" data-testid="cs-detail">
      {lb.node}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="cs-back"><Icons.ChevronLeft className="h-4 w-4" />Retour</button>
      <Card className="overflow-hidden">
        {ws.cover_file_id && <img src={imgUrl(ws.cover_file_id)} alt="" className="h-48 w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
        <div className="p-5">
          <div className="flex items-center gap-2"><span className="font-mono-data text-xs text-slate-400">{ws.reference}</span>
            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">{ws.status_label}</span></div>
          <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">{ws.title}</h1>
          <p className="text-sm text-slate-500">{ws.site_name} · {ws.site_address}</p>
          {ws.status_client_note && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{ws.status_client_note}</p>}
        </div>
      </Card>
      {ws.progress && (
        <Card className="p-4" data-testid="cs-detail-progress">
          <h2 className="mb-4 font-display text-base font-semibold text-slate-800">Suivi de l'intervention</h2>
          <Stepper progress={ws.progress} />
        </Card>
      )}
      {ws.steps?.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Détail des étapes</h2>
          <ul className="space-y-2">{ws.steps.map((s, i) => (
            <li key={i} className="flex items-center gap-2 text-sm"><Icons.CheckCircle2 className={`h-4 w-4 ${s.status === "terminee" ? "text-emerald-600" : "text-slate-300"}`} /><span className="text-slate-700">{s.label}</span><span className="ml-auto text-xs text-slate-400">{s.status_label}</span></li>
          ))}</ul>
        </Card>
      )}
      {ws.events?.length > 0 && (
        <Card className="p-4" data-testid="cs-actualites">
          <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Actualités du chantier</h2>
          <div className="space-y-4">{ws.events.map((e) => {
            const imgs = (e.photos || []).map((p) => ({ src: imgUrl(p.id), caption: p.caption }));
            return (
              <div key={e.id} className="border-l-2 border-emerald-200 pl-3">
                <p className="text-xs text-slate-400">{e.event_date}{e.author_name ? ` · ${e.author_name}` : ""}</p>
                {e.caption && <p className="text-sm text-slate-700">{e.caption}</p>}
                {imgs.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{imgs.map((im, idx) => (
                  <img key={idx} src={im.src} alt="" onClick={() => lb.open(imgs, idx)} className="h-16 w-16 cursor-zoom-in rounded object-cover" data-testid={`cs-event-photo-${e.id}-${idx}`} />
                ))}</div>}
              </div>
            );
          })}</div>
        </Card>
      )}
      {ws.photos?.length > 0 && (
        <Card className="p-4" data-testid="cs-photos">
          <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Photos du chantier</h2>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">{ws.photos.map((p, idx) => (
            <img key={p.id} src={imgUrl(p.id)} alt="" data-testid={`cs-photo-${p.id}`} onClick={() => lb.open(ws.photos.map((x) => ({ src: imgUrl(x.id), caption: x.caption })), idx)} className="h-24 w-full cursor-zoom-in rounded-lg object-cover" />
          ))}</div>
        </Card>
      )}
      {ws.visit_reports?.length > 0 && (
        <Card className="p-4" data-testid="cs-ws-visit-reports">
          <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Rapports de visite</h2>
          <ul className="space-y-1.5">{ws.visit_reports.map((r) => (
            <li key={`${r.kind}-${r.id}`}><button onClick={() => downloadBlob(r.kind === "visit" ? `${API}/clientspace/sites/${r.site_id}/visit/${r.id}/report` : `${API}/clientspace/visit-reports/${r.id}/pdf`, `${r.label}.pdf`)}
              className="flex items-center gap-2 text-sm text-emerald-700 hover:underline" data-testid={`cs-ws-visit-report-${r.id}`}>
              <Icons.ClipboardCheck className="h-4 w-4" />{r.label}{r.date ? ` — ${dfr(r.date)}` : ""}</button></li>))}</ul>
        </Card>
      )}
      {ws.reports?.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Rapports d'intervention</h2>
          <ul className="space-y-1.5">{ws.reports.map((r) => (<li key={r.id}><button onClick={() => downloadBlob(reportUrl(r.id), `rapport-v${r.version}.pdf`)} className="flex items-center gap-2 text-sm text-emerald-700 hover:underline" data-testid={`cs-report-${r.id}`}><Icons.FileText className="h-4 w-4" />Rapport v{r.version}</button></li>))}</ul>
        </Card>
      )}
      <WorksiteInvoices wsId={ws.id} token={token} highlight={hlInvoice} onAsk={(txt) => onRequest("question", txt)} />
      {ws.devis?.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Devis</h2>
          <ul className="space-y-1.5">{ws.devis.map((d) => (<li key={d.id}><button onClick={() => downloadBlob(fileUrl(d.id), `${d.caption}.pdf`)} className="flex items-center gap-2 text-sm text-emerald-700 hover:underline" data-testid={`cs-devis-${d.id}`}><Icons.ReceiptText className="h-4 w-4" />{d.caption}</button></li>))}</ul>
        </Card>
      )}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="font-display text-base font-semibold text-slate-800">Documents</h2>
          <label className="cursor-pointer">
            <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" onChange={onUpload} data-testid="cs-upload-input" />
            <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"><Icons.Upload className="h-4 w-4" />{uploading ? "Envoi…" : "Ajouter (bon de commande…)"}</span>
          </label>
        </div>
        {(!ws.documents || ws.documents.length === 0) ? <Empty text="Aucun document." />
          : <ul className="space-y-1.5">{ws.documents.map((d) => (<li key={d.id}><button onClick={() => downloadBlob(fileUrl(d.id), `${d.caption}`)} className="flex items-center gap-2 text-sm text-emerald-700 hover:underline" data-testid={`cs-doc-${d.id}`}><Icons.FileText className="h-4 w-4" />{d.caption}</button></li>))}</ul>}
      </Card>
      {ws.requests?.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Mes demandes</h2>
          <ul className="space-y-2">{ws.requests.map((r) => (
            <li key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`cs-req-${r.id}`}><p className="text-slate-700">{r.description}</p><p className="mt-1 text-xs text-slate-400">{r.kind === "probleme" ? "Problème signalé" : r.kind === "question" ? "Question" : "Demande de prestation"}</p>{r.client_reply && <p className="mt-1 rounded bg-emerald-50 p-2 text-xs text-emerald-800">Réponse : {r.client_reply}</p>}</li>
          ))}</ul>
        </Card>
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => onRequest("prestation")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cs-request-btn"><Icons.PlusCircle className="mr-1.5 h-4 w-4" />Demander une prestation</Button>
        <Button onClick={() => onRequest("probleme")} variant="outline" data-testid="cs-report-btn"><Icons.TriangleAlert className="mr-1.5 h-4 w-4" />Signaler un problème</Button>
      </div>
    </div>
  );
}

function RequestDialog({ open, onOpenChange, kind, prefill, wsId, auth, onDone }) {
  const [desc, setDesc] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { if (open) setDesc(prefill || ""); }, [open, prefill]);
  const submit = async () => {
    if (!desc.trim()) { toast.error("Veuillez décrire votre demande"); return; }
    setSending(true);
    try { await axios.post(`${API}/clientspace/worksites/${wsId}/requests`, { kind, description: desc }, auth()); toast.success("Demande envoyée. Notre équipe vous recontactera — il ne s'agit pas encore d'un rendez-vous confirmé."); onOpenChange(false); onDone(); }
    catch { toast.error("Envoi impossible"); } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cs-request-dialog">
        <DialogHeader><DialogTitle>{kind === "probleme" ? "Signaler un problème" : kind === "question" ? "Poser une question" : "Demander une prestation"}</DialogTitle><DialogDescription>Votre message est transmis à l'équipe SIGNA CLEAN.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label>Votre message</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} data-testid="cs-request-text" /></div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button><Button onClick={submit} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cs-request-send">Envoyer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GeneralRequestDialog({ open, onOpenChange, auth }) {
  const [opts, setOpts] = useState({ sites: [], prestations: [] });
  const [siteId, setSiteId] = useState("");
  const [newSite, setNewSite] = useState("");
  const [prestation, setPrestation] = useState("");
  const [date, setDate] = useState("");
  const [desc, setDesc] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (open) {
      setDesc(""); setPrestation(""); setNewSite(""); setDate(""); setSiteId("");
      axios.get(`${API}/clientspace/options`, auth()).then((r) => { setOpts(r.data); if (r.data.sites?.length === 1) setSiteId(r.data.sites[0].id); }).catch(() => {});
    }
  }, [open]); // eslint-disable-line
  const isNew = siteId === "__new__";
  const submit = async () => {
    if (!siteId) { toast.error("Choisissez un site"); return; }
    if (isNew && !newSite.trim()) { toast.error("Indiquez le nom du nouveau site"); return; }
    if (!desc.trim()) { toast.error("Décrivez votre demande"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/clientspace/requests`, {
        site_id: isNew ? "" : siteId, site_name: isNew ? newSite.trim() : "",
        prestation, requested_date: date, description: desc }, auth());
      toast.success("Demande envoyée. Notre équipe vous recontactera — il ne s'agit pas encore d'un rendez-vous confirmé."); onOpenChange(false);
    } catch { toast.error("Envoi impossible"); } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cs-gen-request-dialog">
        <DialogHeader><DialogTitle>Demander une visite / intervention</DialogTitle><DialogDescription>Choisissez le site, la date souhaitée et la prestation.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Site concerné</Label>
            <Select value={siteId} onValueChange={setSiteId}><SelectTrigger data-testid="cs-gen-site"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
              <SelectContent>{opts.sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}<SelectItem value="__new__">+ Nouveau site…</SelectItem></SelectContent></Select></div>
          {isNew && <div className="space-y-1.5"><Label>Nom du nouveau site</Label><input value={newSite} onChange={(e) => setNewSite(e.target.value)} placeholder="Ex. Villa Les Palmiers" className="flex h-10 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="cs-gen-newsite" /></div>}
          <div className="space-y-1.5"><Label>Date souhaitée</Label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="flex h-10 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="cs-gen-date" /></div>
          <div className="space-y-1.5"><Label>Prestation souhaitée</Label>
            <Select value={prestation} onValueChange={setPrestation}><SelectTrigger data-testid="cs-gen-prestation"><SelectValue placeholder="Sélectionner une prestation" /></SelectTrigger><SelectContent>{opts.prestations.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Détails de votre demande</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} data-testid="cs-gen-text" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button><Button onClick={submit} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cs-gen-send">Envoyer la demande</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function groupDocs(docs) {
  const map = new Map();
  for (const d of docs) {
    const key = d.group_key || "autres";
    if (!map.has(key)) map.set(key, { key, label: d.group_label || "Autres documents", items: [] });
    map.get(key).items.push(d);
  }
  return Array.from(map.values());
}

function LoginScreen({ brand, onToken }) {
  const [ident, setIdent] = useState("");
  const [busy, setBusy] = useState(false);

  const login = async () => {
    if (!ident.trim()) { toast.error("Saisissez votre e-mail, téléphone ou SIRET"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/clientspace/auth/login`, { identifier: ident.trim() });
      if (data.device_token) localStorage.setItem(CS_DEVICE, data.device_token);
      localStorage.setItem(CS_IDENT, ident.trim());
      onToken(data.session_token);
    } catch (e) { toast.error(e.response?.data?.detail || "Connexion impossible"); }
    finally { setBusy(false); }
  };

  const LogoImg = brand.logo_b64
    ? <img src={`data:image/png;base64,${brand.logo_b64}`} alt={brand.name || "SIGNA CLEAN"} className="h-11 w-auto object-contain drop-shadow-lg sm:h-14" />
    : <img src="/signa-logo.png" alt="SIGNA CLEAN" className="h-11 w-auto object-contain drop-shadow-lg sm:h-14" />;

  const PILLS = [
    { icon: "FileSignature", label: "Devis" },
    { icon: "CalendarCheck", label: "Interventions" },
    { icon: "FolderOpen", label: "Documents" },
  ];

  return (
    <div className="relative min-h-[100svh] w-full overflow-hidden bg-[#0D2E24]" data-testid="cs-login-page">
      <img src={CLIENT_BG_PORTRAIT} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-center lg:hidden" loading="eager" />
      <img src={CLIENT_BG_LANDSCAPE} alt="" aria-hidden="true" className="absolute inset-0 hidden h-full w-full object-cover object-center lg:block" loading="eager" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D2E24]/60 via-[#0D2E24]/20 to-transparent" aria-hidden="true" />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col px-5 py-8 lg:flex-row lg:items-stretch lg:gap-12 lg:px-10">
        {/* Colonne gauche : logo officiel, accroche, repères */}
        <div className="flex flex-1 flex-col lg:py-12">
          <div className="flex justify-center lg:justify-start">{LogoImg}</div>
          <div className="hidden flex-1 flex-col justify-center lg:flex">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#A3E635]">Votre espace client</p>
            <div className="mt-3 h-[3px] w-14 bg-[#A3E635]" />
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] text-white drop-shadow-xl sm:text-5xl lg:text-6xl">
              Votre projet,<br />en toute simplicité.
            </h1>
            <p className="mt-5 max-w-md text-base text-white/90 drop-shadow">
              Retrouvez vos devis, suivez vos interventions et consultez vos documents.
            </p>
          </div>
          <div className="mt-6 hidden flex-wrap items-center gap-x-5 gap-y-2 lg:flex">
            {PILLS.map((p) => {
              const I = Icons[p.icon];
              return (
                <span key={p.label} className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-white/90 drop-shadow">
                  <I className="h-4 w-4 text-[#A3E635]" /> {p.label}
                </span>
              );
            })}
          </div>
          <div className="mt-5 hidden flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium text-white/70 lg:flex">
            <span className="uppercase tracking-wider">{brand.slogan || "sécuriser, valoriser, innover"}</span>
            {brand.phone && <><span aria-hidden="true">·</span><a href={`tel:${(brand.phone || "").replace(/\s/g, "")}`} className="hover:text-white">{brand.phone}</a></>}
            {brand.website && <><span aria-hidden="true">·</span><a href={brand.website} className="hover:text-white">{(brand.website || "").replace(/^https?:\/\//, "")}</a></>}
          </div>
        </div>

        {/* Colonne droite : carte de connexion client */}
        <div className="flex items-center justify-center lg:w-[440px]">
          <div className="w-full max-w-[460px] rounded-2xl border border-white/50 bg-white/85 p-6 shadow-2xl backdrop-blur-[18px] [-webkit-backdrop-filter:blur(18px)] supports-[backdrop-filter]:bg-white/70 sm:p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">{brand.name || "SIGNA CLEAN"}</p>
            <h2 className="mt-1 font-display text-2xl font-bold text-[#0D2E24] sm:text-3xl">Connexion à votre espace client</h2>
            <p className="mt-1.5 text-sm text-slate-600">Heureux de vous retrouver.</p>

              <div className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="cs-ident" className="text-[#0D2E24]">E-mail, téléphone ou SIRET</Label>
                  <input id="cs-ident" value={ident} onChange={(e) => setIdent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()}
                    placeholder="vous@exemple.fr · 0696… · SIRET" autoComplete="username"
                    className="flex h-12 w-full rounded-md border border-slate-300 bg-white/80 px-3 text-base text-[#0D2E24] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    data-testid="cs-login-identifier" />
                  <p className="text-xs text-slate-500">E-mail, téléphone ou SIRET connu de SIGNA CLEAN.</p>
                </div>
                <Button onClick={login} disabled={busy} className="h-12 w-full bg-emerald-700 text-base font-semibold hover:bg-emerald-800" data-testid="cs-login-send-code">
                  {busy ? <Icons.Loader2 className="h-5 w-5 animate-spin" />
                    : <span className="flex items-center justify-center gap-2">Accéder à mon espace <Icons.ArrowRight className="h-5 w-5" /></span>}
                </Button>
              </div>

            <div className="mt-5 border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-500"><span className="font-medium text-slate-600">Première connexion ?</span> Saisissez l'e-mail, le téléphone ou le SIRET communiqué à SIGNA CLEAN.</p>
              <a href="mailto:contact@signaclean.com" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline" data-testid="cs-login-help">
                <Icons.LifeBuoy className="h-4 w-4" /> Besoin d'aide ? Contactez-nous
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
